import { useState } from "react";
import { SchoolFeedback } from "components/Molecules/GiveFeedback/SchoolFeedback/SchoolFeedback";
import { DateTime } from "luxon";
import { FeedbackModel, IntervalModel, SchoolModel } from "model";
import { Alert, Colors } from "swing-components";
import { useShouldRestrictAdmin } from "utils/hooks";
import { COMBINED_RESTRICTIONS, RESTRICTIONS } from "utils/security-restrictions";

import { Button } from "~components/Molecules";
import { translate } from "~utils";

import { IssueList } from "../IssueList/IssueList";
import { GiveFeedback, type SubLastFeedback } from "./GiveFeedback/GiveFeedback";
import { useReportIssueModal } from "./hooks";

type AdminExperiencePropsBase = {
  school: SchoolModel;
  intervals: IntervalModel[];
  requestId: string;
  status: string;
};

type AdminExperienceProps = AdminExperiencePropsBase & {
  // These are optional types. A cancelled request can
  // be both filled and unfilled, and so the sub data
  // is passed in only on a filled request for reporting issues.
  subId?: string;
  subName?: string;
  subProfileImgUrl?: string;
  subIsSwingHero?: boolean;
  hasFeedback?: boolean;
  feedback?: FeedbackModel[];
  subLastFeedback?: SubLastFeedback;
};

export type ReportIssueButtonProps = AdminExperiencePropsBase & {
  // The types cannot be optional here. We're passing these
  // values directly into the hook, hence the two different
  // types.
  subId: string;
  subName: string;
};

export function AdminExperience(props: AdminExperienceProps) {
  const {
    intervals,
    requestId,
    status,
    subId,
    subName,
    hasFeedback = false,
    feedback,
    school,
    subIsSwingHero,
    subProfileImgUrl,
    subLastFeedback,
  } = props;

  // Used to drive optimistic UI updates and layout:
  // `undefined` = untouched,
  // `false` = successfully submitted,
  // `true` = submission failed, show error state
  const [hasFeedbackSubmissionError, setHasFeedbackSubmissionError] = useState<
    boolean | undefined
  >();

  const firstIntervalStart = DateTime.fromISO(intervals[0].localStart).startOf("day");
  const now = DateTime.now().startOf("day");
  const isClassroomRequester = useShouldRestrictAdmin([COMBINED_RESTRICTIONS.CLASSROOM_ADMIN]);
  const shouldHideFeedback = useShouldRestrictAdmin([RESTRICTIONS.HIDE_SUB_FEEDBACK]);

  // Feedback is an array of objects, and a request can have
  // more than one feedback entry. This checks if at least one
  // feedback object has `preferred` set to true,
  // indicating that the sub in question was favorited.
  const isFavoritedSub = hasFeedback ? feedback?.some((f) => f.preferred) : undefined;

  if (firstIntervalStart > now) {
    // We don't want to be able to report issues for
    // requests that haven't started yet.
    return <></>;
  }

  if (!subId || !subName) {
    return <></>;
  }

  return (
    <>
      {status !== "FILLED" && !shouldHideFeedback && (
        <div
          css={{
            marginTop: 16,
            ...(subLastFeedback && {
              borderTop: `1px solid ${Colors.slate300}`,
              borderBottom: `1px solid ${Colors.slate300}`,
              padding: "16px 0",
            }),
          }}
        >
          {/* Error message for repeat review submission */}
          {hasFeedbackSubmissionError && (
            <div css={{ marginBottom: 16 }}>
              <Alert showIcon color="danger">
                <p
                  css={{ margin: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: translate("GIVE_FEEDBACK_REPEAT_REVIEW_SUBMISSION_ERROR_MSG"),
                  }}
                />
              </Alert>
            </div>
          )}

          <GiveFeedback
            school={school}
            subId={subId}
            subName={subName}
            subProfileImgUrl={subProfileImgUrl}
            subIsSwingHero={subIsSwingHero}
            requestId={requestId}
            intervals={intervals}
            status={status}
            isFavoritedSub={isFavoritedSub}
            initiationType="START_WITH_BUTTON"
            subLastFeedback={subLastFeedback}
            feedback={feedback}
            hasFeedback={hasFeedback}
            hasFeedbackSubmissionError={hasFeedbackSubmissionError}
            setHasSubmissionError={setHasFeedbackSubmissionError}
          />
        </div>
      )}
      {!isClassroomRequester && (
        <div css={{ margin: "16px 0" }}>
          <ReportIssueButton
            intervals={intervals}
            requestId={requestId}
            school={school}
            status={status}
            subId={subId}
            subName={subName}
          />
        </div>
      )}
      <IssueList
        school={school}
        intervals={intervals}
        requestId={requestId}
        status={status}
        subId={subId}
        subName={subName}
        variant="TRUNCATED"
      />
      {feedback && feedback?.length > 0 && !shouldHideFeedback && (
        <>
          <div css={{ borderBottom: `1px solid ${Colors.slate300}`, marginBottom: 16 }} />
          <SchoolFeedback feedback={feedback} />
        </>
      )}
    </>
  );
}

/*** Helper Components ***/
function ReportIssueButton(props: ReportIssueButtonProps) {
  const { intervals, requestId, status, subId, subName, school } = props;
  const { presentModal } = useReportIssueModal({
    school,
    subId,
    subName,
    intervals,
    status,
    requestId,
  });
  return (
    <Button
      type="SECONDARY"
      isFluid
      label={translate("SUB_SUMMARY_BUTTON_REPORT_AN_ISSUE")}
      onClick={presentModal}
    />
  );
}
